/*
 * 戒导航 · 博客数据统计（jiedh/blog-stats）前台「相关文章推荐」组件
 * =============================================================================
 * 设计依据：doc/博客数据统计与排序设计方案.md §7
 *
 * 用户诉求（2026-10-29）：
 *   「每篇博客文章底部推荐 6 篇同标签、相近的文章，按点赞量和浏览量排序，
 *     且是该用户没有读过的，放在评论区上方，读完就能接着读。」
 *
 * 职责边界：
 *   · 本组件**只负责展示**：取数、排序、排除已读全部由服务端
 *     （Controller\RelatedArticlesController）完成，前端不做二次筛选与排序 ——
 *     保证「谁读过什么」这类与身份相关的判断只有一份实现；
 *   · 只在博客详情页（BlogItem.articleItems 注入）使用，列表页不加载。
 *
 * 为什么返回 null 而不是空壳：
 *   候选不足（标签少 / 都读过了）时整块**不渲染**，宁可不出现，也不显示一个
 *   「暂无推荐」的空盒子打断阅读节奏。
 *
 * ⚠️【坑，务必保留这条注释】不要给本组件传 vnode key：
 *   vendor 的 BlogItem.articleItems() 把 `content`(100) / `comments`(75) 两个 vnode
 *   塞进同一个 fragment 数组，**它们都没有 key**；Mithril 2 在 normalizeChildren
 *   里强制「同一 fragment 内要么全部有 key，要么全部没有」，混用会直接抛
 *     TypeError: In fragments, vnodes must either all have keys or none have keys.
 *   该异常发生在 redraw 的 patch 阶段，会导致**整页渲染中断**（文章页永远停在
 *   骨架屏、控制台只剩一条被序列化成 {} 的 Error），极难定位。2026-10-29 已踩过。
 *
 * 因此「切换文章后不能串数据」改用 onbeforeupdate 判断文章 id 变化后重新取数，
 *   并用 loadedId 做请求竞态防护（旧请求回来时如果文章已经换了就丢弃结果）。
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import extractText from 'flarum/common/utils/extractText';

/** 服务端返回的单条推荐（字段口径见 RelatedArticlesController::handle 第 6 步） */

export default class RelatedArticles extends Component {
  items = [];
  /** 首次请求未返回前为 true，用于渲染骨架（保持版面高度稳定，避免布局跳动） */
  loading = true;
  /** 请求失败标记：失败时静默隐藏整块，不打扰阅读 */
  failed = false;
  /** 当前数据对应的文章 id：用于「换文章要重新取数」与「旧请求竞态丢弃」 */
  loadedId = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.load(vnode.attrs.article);
  }

  /**
   * 站内跳转（推荐位点进去 → 另一篇文章）会复用同一个组件实例，
   * 这里比对文章 id：变了就重置状态并重新取数，避免 A 文章的推荐显示在 B 文章底部。
   */
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    const id = String(vnode.attrs?.article?.id?.() ?? '');
    if (id !== this.loadedId) this.load(vnode.attrs.article);
  }

  /**
   * 拉取推荐列表。
   *
   * 端点：GET {baseUrl}/blog-stats/related/{discussionId}
   * 失败策略：推荐不是关键路径 —— 吞掉错误并隐藏整块（errorHandler 抑制核心弹窗）。
   */
  load(article) {
    const id = article?.id?.();
    if (!id) {
      this.loading = false;
      return;
    }
    const requestId = String(id);

    // 换文章：清空上一批数据并回到骨架态，避免闪出旧文章标题
    this.loadedId = requestId;
    this.items = [];
    this.failed = false;
    this.loading = true;
    const base = String(app.forum.attribute('baseUrl') || '').replace(/\/+$/, '');
    app.request({
      method: 'GET',
      url: `${base}/blog-stats/related/${requestId}`,
      errorHandler: () => undefined
    }).then(res => {
      // 竞态防护：请求飞行期间用户又换了文章，本次结果直接丢弃
      if (this.loadedId !== requestId) return;
      this.items = res?.data?.items ?? [];
    }).catch(() => {
      if (this.loadedId !== requestId) return;
      this.failed = true;
      this.items = [];
    }).then(() => {
      if (this.loadedId !== requestId) return;
      this.loading = false;
      m.redraw();
    });
  }

  /** 数字展示：千分位；脏值一律回退 0（服务端理论上已给整数，这里再做一层兜底） */
  formatCount(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return '0';
    return n.toLocaleString();
  }
  view() {
    // 请求失败、或确实没有可推荐的文章 ⇒ 整块不渲染
    if (this.failed || !this.loading && this.items.length === 0) return null;
    return m("section", {
      className: "JiedhRelated",
      "aria-labelledby": "jiedh-related-title"
    }, m("h4", {
      className: "JiedhRelated-title",
      id: "jiedh-related-title"
    }, m(Icon, {
      name: "fas fa-book-open"
    }), extractText(app.translator.trans('jiedh-blog-stats.forum.related_title'))), this.loading ? m("ul", {
      className: "JiedhRelated-list",
      "aria-busy": "true"
    }, [0, 1, 2, 3, 4, 5].map(i => m("li", {
      className: "JiedhRelated-item JiedhRelated-item--skeleton",
      key: `sk-${i}`
    }, m("span", {
      className: "JiedhRelated-item-title"
    }, "\xA0"), m("span", {
      className: "JiedhRelated-item-meta"
    }, "\xA0")))) : m("ul", {
      className: "JiedhRelated-list"
    }, this.items.map(item => m("li", {
      className: "JiedhRelated-item-wrap",
      key: `rel-${item.id}`
    }, m(Link, {
      className: "JiedhRelated-item",
      href: app.route('blogArticle', {
        id: `${item.slug}`
      })
    }, m("span", {
      className: "JiedhRelated-item-title"
    }, item.title), m("span", {
      className: "JiedhRelated-item-meta"
    }, m("span", {
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.views'))
    }, m(Icon, {
      name: "far fa-eye"
    }), " ", this.formatCount(item.viewCount)), m("span", {
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.likes'))
    }, m(Icon, {
      name: "far fa-thumbs-up"
    }), " ", this.formatCount(item.likeCount)), m("span", {
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.comments'))
    }, m(Icon, {
      name: "far fa-comment"
    }), " ", this.formatCount(item.commentCount))))))));
  }
}
flarum.reg.add('jiedh-blog-stats', 'forum/components/RelatedArticles', RelatedArticles);